import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  ColorPicker,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  TimePicker,
  message
} from "antd";
import dayjs from "dayjs";
import { createSprNumberSpr, fetchSprNumberSpr, saveSprNumberSpr } from "./spr-number-spr-api";
import {
  DISCOUNT_ID1_OPTIONS,
  MEAL_FLAGS,
  getVidLabel,
  isExtraPlaceVid,
  isOkIsLech,
  mealFlagsToRazmer,
  normalizeHtmlColor,
  normalizeRow,
  razmerIsNumber,
  razmerToMealFlags,
} from "./spr-number-spr-utils";

const LABEL_WIDTH = 200;

const formItemLayout = {
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto" },
  labelAlign: "right"
};

const toTime = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const timeToIso = (value) => {
  if (!value || !value.isValid()) return null;
  return value.second(0).millisecond(0).toISOString();
};

/**
 * Форма элемента справочника. Delphi: UESpr / UESprDiscount / UESprTipPit.
 */
const SprNumberSprCard = ({ vid, id, isNew = false, open, onSaved, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isDiscount = vid === 0;
  const isMeal = vid === 5;
  const isVoucher = vid === 1;
  const isExtraPlace = isExtraPlaceVid(vid);
  const okLabel = isOkIsLech(vid) ? "Лечебная" : "Доп.";
  const razmerLabel = razmerIsNumber(vid) ? "Номер" : "Размер";

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    if (isNew) {
      setLoading(false);
      setError("");
      const empty = {
        name: "",
        razmer: null,
        isHide: false,
        isOk: false,
        id1: 0,
        date1: null,
        date2: null,
        code: isExtraPlace ? "#9a3412" : "",
        breakfast: false,
        lunch: false,
        snack: false,
        dinner: false
      };
      form.setFieldsValue(empty);
      return () => {
        cancelled = true;
      };
    }

    if (id == null) return undefined;

    setLoading(true);
    setError("");
    fetchSprNumberSpr(vid, id)
      .then((row) => {
        if (cancelled) return;
        const card = normalizeRow(row);
        const meals = razmerToMealFlags(card.razmer);
        form.setFieldsValue({
          id: card.id,
          name: card.name,
          razmer: card.razmer,
          isHide: card.isHide,
          isOk: card.isOk,
          id1: card.id1,
          date1: toTime(card.date1),
          date2: toTime(card.date2),
          code: normalizeHtmlColor(card.code) || (isExtraPlace ? "#9a3412" : ""),
          ...meals
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить запись");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, vid, id, form]);

  const onFinish = async (values) => {
    setSaving(true);
    setError("");

    let razmer = values.razmer ?? null;
    if (isMeal) {
      razmer = mealFlagsToRazmer(values);
    }

    const body = {
      vid,
      name: (values.name || "").trim(),
      razmer,
      isHide: values.isHide ? 1 : 0,
      isOk: values.isOk ? 1 : 0,
      id1: isDiscount ? (values.id1 ?? 0) : (values.id1 ?? 0),
      id2: 0,
      date1: isVoucher ? timeToIso(values.date1) : null,
      date2: isVoucher ? timeToIso(values.date2) : null,
      code: isExtraPlace ? normalizeHtmlColor(values.code) || null : null
    };

    try {
      if (isNew) {
        const created = await createSprNumberSpr(body);
        message.success("Сохранено");
        onSaved?.(created);
      } else {
        const saved = await saveSprNumberSpr({ ...body, id });
        message.success("Сохранено");
        onSaved?.(saved);
      }
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="spr-number-spr-card__center">
        <Spin tip="Загрузка..." />
      </div>
    );
  }

  return (
    <div className="spr-number-spr-card">
      <div className="spr-number-spr-card__body">
        {error ? (
          <Alert className="spr-number-spr-card__error" type="error" showIcon message={error} />
        ) : null}
        <Form
          form={form}
          className="spr-number-spr-card__form"
          {...formItemLayout}
          colon={false}
          onFinish={onFinish}
          disabled={saving}
        >
          {!isNew ? (
            <Form.Item label="#" name="id">
              <Input disabled />
            </Form.Item>
          ) : null}
          <Form.Item label="Справочник">
            <Input value={getVidLabel(vid)} disabled />
          </Form.Item>
          <Form.Item
            label="Наименование"
            name="name"
            rules={[{ required: true, message: "Укажите наименование" }]}
          >
            <Input maxLength={80} />
          </Form.Item>

          {isMeal ? (
            <Form.Item label="Приёмы пищи">
              <div className="spr-number-spr-card__meals">
                {MEAL_FLAGS.map((m) => (
                  <Form.Item key={m.key} name={m.key} valuePropName="checked" noStyle>
                    <Checkbox>{m.label}</Checkbox>
                  </Form.Item>
                ))}
              </div>
            </Form.Item>
          ) : null}

          {isDiscount ? (
            <>
              <Form.Item label={razmerLabel} name="razmer">
                <InputNumber style={{ width: "100%" }} precision={0} />
              </Form.Item>
              <Form.Item label="Применение" name="id1">
                <Select options={DISCOUNT_ID1_OPTIONS} />
              </Form.Item>
            </>
          ) : null}

          {!isMeal && !isDiscount ? (
            <>
              <Form.Item label={razmerLabel} name="razmer">
                <InputNumber style={{ width: "100%" }} precision={0} />
              </Form.Item>
              <Form.Item label={okLabel} name="isOk" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </>
          ) : null}

          {isExtraPlace ? (
            <Form.Item
              label="Цвет"
              name="code"
              getValueFromEvent={(c) => (c?.toHexString ? c.toHexString() : c)}
              getValueProps={(v) => ({ value: normalizeHtmlColor(v) || "#9a3412" })}
            >
              <ColorPicker showText format="hex" disabled={saving} />
            </Form.Item>
          ) : null}

          {isVoucher ? (
            <>
              <Form.Item label="Время с" name="date1">
                <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
              </Form.Item>
              <Form.Item label="Время по" name="date2">
                <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
              </Form.Item>
            </>
          ) : null}

          <Form.Item label="Скрыть" name="isHide" valuePropName="checked">
            <Checkbox />
          </Form.Item>
        </Form>
      </div>
      <div className="spr-number-spr-card__footer">
        <Button type="primary" loading={saving} onClick={() => form.submit()}>
          Сохранить
        </Button>
        <Button onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
    </div>
  );
};

export default SprNumberSprCard;
